import { useState } from "react";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  Ticket,
  Users,
  Minus,
  Plus,
  ShoppingCart,
  Calendar,
  Clock,
  MapPin,
  Check,
  Info,
} from "lucide-react";

function formatCurrency(currency) {
  if (!currency) return "GH₵";
  const c = String(currency).toUpperCase();
  if (c === "USD") return "$";
  if (c === "GHS") return "GH₵";
  return currency;
}

function formatDate(dateString) {
  if (!dateString) return "Date TBA";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "Date TBA";
  }
}

function formatTime(timeString) {
  if (!timeString) return "";
  try {
    const [hours, minutes] = timeString.split(":");
    const hour12 = parseInt(hours, 10) % 12 || 12;
    const ampm = parseInt(hours, 10) >= 12 ? "PM" : "AM";
    return `${hour12}:${minutes || "00"} ${ampm}`;
  } catch {
    return timeString;
  }
}

export default function SelectTicketStep({
  event,
  selectedTicket,
  quantity = 1,
  onSelectTicket,
  onQuantityChange,
  onContinue,
  onBack,
  singleTicketOnly = false,
}) {
  const ticketTypes = event?.ticket_types || [];
  const effectiveQuantity = singleTicketOnly ? 1 : quantity;
  const totalAmount = selectedTicket ? selectedTicket.price * effectiveQuantity : 0;

  const handleContinue = () => {
    if (!selectedTicket) return;
    if (!singleTicketOnly && quantity < 1) return;
    onContinue?.({ ticketType: selectedTicket, quantity: effectiveQuantity, totalAmount });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      {/* Header - aligned with consumer EventTicketSelection */}
      <div className="sticky top-0 z-20 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 shadow-2xl">
        <div className="relative px-4 py-8 sm:px-6 lg:px-8">
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                backgroundSize: "40px 40px",
              }}
            />
          </div>

          <div className="relative mx-auto max-w-6xl">
            <div className="flex gap-3 items-center mb-6">
              <motion.button
                onClick={onBack}
                whileHover={{ x: -4, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex gap-2 items-center px-5 py-2.5 text-sm font-semibold text-white bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 shadow-lg transition-all hover:bg-white/30 hover:shadow-xl"
              >
                <ChevronLeft size={18} />
                Back to client selection
              </motion.button>
            </div>

            <div className="flex gap-4 items-center">
              <div className="flex justify-center items-center w-16 h-16 rounded-2xl border-2 shadow-2xl backdrop-blur-md bg-white/20 border-white/30">
                <Ticket size={32} className="text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-extrabold text-white drop-shadow-lg sm:text-3xl">
                  Select tickets (for client)
                </h1>
                <p className="mt-1 text-sm text-indigo-100 sm:text-base">
                  {event?.event_title}
                </p>
              </div>
            </div>

            {/* Event quick info */}
            <div className="flex flex-wrap gap-4 mt-6 text-sm">
              <div className="flex gap-2 items-center px-4 py-2 rounded-lg border backdrop-blur-sm bg-white/10 border-white/20">
                <Calendar className="text-white" size={16} />
                <span className="font-medium text-white">{formatDate(event?.event_date)}</span>
              </div>
              {event?.event_time && (
                <div className="flex gap-2 items-center px-4 py-2 rounded-lg border backdrop-blur-sm bg-white/10 border-white/20">
                  <Clock className="text-white" size={16} />
                  <span className="text-white">{formatTime(event.event_time)}</span>
                </div>
              )}
              {event?.venue_name && (
                <div className="flex gap-2 items-center px-4 py-2 rounded-lg border backdrop-blur-sm bg-white/10 border-white/20">
                  <MapPin className="text-white" size={16} />
                  <span className="text-white">{event.venue_name}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Left column - ticket selection */}
            <div className="lg:col-span-2">
              {/* Step indicator */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-3 items-center p-4 mb-8 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-200 shadow-md"
              >
                <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-lg">
                  <span className="text-lg font-bold text-white">2</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-emerald-900">Step 2 of 3</p>
                  <p className="text-xs text-emerald-700">Choose ticket for your client</p>
                </div>
              </motion.div>

              {/* Ticket types */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h2 className="mb-6 text-xl font-bold text-gray-900">Available tickets</h2>
                <div className="space-y-4">
                  {ticketTypes.map((ticket, index) => {
                    const isSelected = selectedTicket?.name === ticket.name;
                    return (
                      <motion.div
                        key={ticket.name ?? index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.01 }}
                        onClick={() => onSelectTicket?.(ticket)}
                        className={`relative overflow-hidden p-6 rounded-2xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-500 ring-2 ring-emerald-300 shadow-xl"
                            : "bg-white border-gray-200 hover:border-emerald-300 hover:shadow-lg"
                        }`}
                      >
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="flex absolute top-4 right-4 justify-center items-center w-8 h-8 bg-indigo-500 rounded-full shadow-lg"
                          >
                            <Check size={18} className="text-white" />
                          </motion.div>
                        )}

                        <div className="flex gap-4 items-start">
                          <div
                            className={`flex-shrink-0 p-3 rounded-xl ${isSelected ? "bg-emerald-500" : "bg-gray-100"}`}
                          >
                            <Ticket size={24} className={isSelected ? "text-white" : "text-gray-600"} />
                          </div>
                          <div className="flex-1">
                            <h3
                              className={`text-lg font-bold mb-2 ${isSelected ? "text-emerald-900" : "text-gray-900"}`}
                            >
                              {ticket.name}
                            </h3>
                            {ticket.description && (
                              <p
                                className={`text-sm mb-4 ${isSelected ? "text-emerald-700" : "text-gray-600"}`}
                              >
                                {ticket.description}
                              </p>
                            )}

                            {ticket.whats_included?.length > 0 && (
                              <div className="mb-4 space-y-2">
                                {ticket.whats_included.slice(0, 3).map((item, idx) => (
                                  <div key={idx} className="flex gap-2 items-start text-sm">
                                    <Check size={16} className="flex-shrink-0 mt-0.5 text-emerald-600" />
                                    <span className={isSelected ? "text-emerald-800" : "text-gray-700"}>
                                      {item}
                                    </span>
                                  </div>
                                ))}
                                {ticket.whats_included.length > 3 && (
                                  <p className="ml-6 text-xs font-medium text-emerald-600">
                                    +{ticket.whats_included.length - 3} more benefits
                                  </p>
                                )}
                              </div>
                            )}

                            <div className="flex gap-1 items-baseline">
                              <span className="text-sm font-medium text-gray-600">
                                {formatCurrency(ticket.currency)}
                              </span>
                              <span
                                className={`text-2xl font-bold ${isSelected ? "text-emerald-600" : "text-gray-900"}`}
                              >
                                {ticket.price}
                              </span>
                              <span className="text-sm text-gray-500">per ticket</span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>

              {/* Quantity selector - hidden for conferences (singleTicketOnly) */}
              {selectedTicket && !singleTicketOnly && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6 mt-8 bg-white rounded-2xl border-2 border-gray-200 shadow-lg"
                >
                  <div className="flex gap-3 items-center mb-6">
                    <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-md">
                      <Users size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Number of tickets</h3>
                      <p className="text-sm text-gray-600">How many for this client?</p>
                    </div>
                  </div>

                  <div className="flex gap-4 justify-center items-center">
                    <motion.button
                      type="button"
                      onClick={() => onQuantityChange?.(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      whileHover={quantity > 1 ? { scale: 1.1 } : {}}
                      whileTap={quantity > 1 ? { scale: 0.9 } : {}}
                      className={`flex justify-center items-center w-12 h-12 rounded-xl border-2 transition-all ${
                        quantity <= 1
                          ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-white border-emerald-300 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-500"
                      }`}
                    >
                      <Minus size={20} />
                    </motion.button>

                    <div className="flex flex-col items-center px-8 py-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border-2 border-emerald-300">
                      <span className="text-4xl font-bold text-emerald-600">{quantity}</span>
                      <span className="text-sm font-medium text-emerald-700">
                        {quantity === 1 ? "Ticket" : "Tickets"}
                      </span>
                    </div>

                    <motion.button
                      type="button"
                      onClick={() => onQuantityChange?.(quantity + 1)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl border-2 border-emerald-600 shadow-md transition-all hover:shadow-lg"
                    >
                      <Plus size={20} className="text-white" />
                    </motion.button>
                  </div>

                  {quantity > 1 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="flex gap-3 items-start p-4 mt-6 bg-blue-50 rounded-xl border border-blue-200"
                    >
                      <Info size={20} className="flex-shrink-0 text-blue-600 mt-0.5" />
                      <div className="text-sm">
                        <p className="mb-1 font-semibold text-blue-900">Multiple tickets</p>
                        <p className="text-blue-700">
                          You are registering {quantity} ticket{quantity > 1 ? "s" : ""} for this client.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </div>

            {/* Right column - order summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="sticky top-32 p-6 bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 shadow-xl"
              >
                <div className="flex gap-3 items-center pb-6 mb-6 border-b-2 border-gray-200">
                  <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-md">
                    <ShoppingCart size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Order summary</h3>
                </div>

                {selectedTicket ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="space-y-4"
                  >
                    <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                      <p className="mb-2 text-xs font-bold tracking-wide text-emerald-700 uppercase">
                        Ticket type
                      </p>
                      <p className="text-lg font-bold text-emerald-900">{selectedTicket.name}</p>
                    </div>

                    <div className="p-4 bg-white rounded-xl border border-gray-200">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-600">Price per ticket</span>
                        <span className="text-base font-bold text-gray-900">
                          {formatCurrency(selectedTicket.currency)} {selectedTicket.price}
                        </span>
                      </div>
                      {!singleTicketOnly && (
                        <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Quantity</span>
                          <span className="text-base font-bold text-gray-900">{effectiveQuantity}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center mt-3">
                        <span className="text-sm font-bold text-gray-700">Subtotal</span>
                        <span className="text-lg font-bold text-gray-900">
                          {formatCurrency(selectedTicket.currency)} {totalAmount}
                        </span>
                      </div>
                    </div>

                    <div className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border-2 border-emerald-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold tracking-wide text-emerald-700 uppercase">
                          Total amount
                        </span>
                        <span className="text-2xl font-bold text-emerald-600">
                          {formatCurrency(selectedTicket.currency)} {totalAmount}
                        </span>
                      </div>
                    </div>

                    <motion.button
                      type="button"
                      onClick={handleContinue}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-4 w-full text-base font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl shadow-xl transition-all hover:from-emerald-600 hover:to-teal-700 hover:shadow-2xl"
                    >
                      Continue to registration
                    </motion.button>
                  </motion.div>
                ) : (
                  <div className="p-8 text-center bg-gray-50 rounded-xl border-2 border-gray-300 border-dashed">
                    <Ticket size={48} className="mx-auto mb-4 text-gray-400" />
                    <p className="text-sm font-medium text-gray-600">Select a ticket type to continue</p>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
